/**
 * Application form state management using React Context
 * Provides centralized state management for the multi-step application form
 */

import * as React from "react";
import type {
  ApplicationFormData,
  ApplicationStep,
  StepConfig,
  BorrowerData,
  HomeData,
  LandData,
  PricingData,
  LenderConfig,
  SignatureData,
  SignatureStatus,
  LenderSubmission,
  Address,
  Employment,
  FinancialAccount,
  AdditionalIncome,
  Expense,
} from "../types/applicationTypes";
import {
  createEmptyApplication,
  createEmptyBorrower,
  isApplicationLockedForSigning,
} from "../types/applicationTypes";
import {
  computeApplicationOverallProgress,
  computeApplicationStepProgress,
} from "../validation/validateApplication";
import { getFieldValueSnapshot } from "../autosave/fieldSnapshot";

// ============================================================================
// Types
// ============================================================================

export interface ApplicationFormState {
  /** Current application data */
  data: ApplicationFormData;
  /** Current step in the wizard */
  currentStep: ApplicationStep;
  /** Whether the form is loading */
  isLoading: boolean;
  /** Whether there are unsaved changes */
  isDirty: boolean;
  /** Fields that have changed since last save */
  dirtyFields: Set<string>;
  /** Last save timestamp */
  lastSavedAt: string | null;
  /** Error message if any */
  error: string | null;
  /** Field-level errors (e.g. duplicate email on draft creation) */
  fieldErrors: Record<string, string>;
  /** Current validation errors used for signing readiness and progress */
  validationErrors: Record<string, string>;
  /** Latest backend validation_failures snapshot from calculate-progress */
  backendValidationErrors: Record<string, string>;
  /** True after the user clicks "Check required fields" in this session */
  manualFieldValidationActive: boolean;
  /** Whether the primary borrower passed frontend validation (after manual "Check required fields") */
  frontendValidationPrimaryPassed: boolean;
  /** Whether the co-borrower passed frontend validation (after manual "Check required fields") */
  frontendValidationCoPassed: boolean;
  /** Lender submission states */
  lenderSubmissions: LenderSubmission[];
  /** Server IDs of entities pending deletion, keyed by "borrowerType.entityType" */
  pendingEntityDeletes: Record<string, string[]>;
  /** Whether the backend-calculated loan amount is being fetched */
  loanAmountLoading: boolean;
  /** Whether the application is locked by another user */
  isLockedByAnotherUser: boolean;
  /** User ID of the user who has the lock */
  lockedByUserId: string | null;
  /** Field paths excluded from background autosave until re-edited */
  pausedAutoSaveFields: Set<string>;
  /** Serialized values at pause time (for detecting re-edit) */
  pausedAutoSaveSnapshots: Record<string, string>;
  /** Duplicate warning messages by field path (e.g., "primaryBorrower.email" -> "Coborrower already exists...") */
  duplicateWarnings: Record<string, string>;
  /** Fields that have been acknowledged and should retry with ignore_duplicate=true */
  acknowledgedDuplicateFields: Set<string>;
}

export interface ApplicationFormActions {
  // Navigation
  setCurrentStep: (step: ApplicationStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;

  // Data operations
  initializeApplication: (applicationId?: string) => Promise<void>;
  resetApplication: () => void;
  setIsLoading: (loading: boolean) => void;
  setLoanAmountLoading: (loading: boolean) => void;
  /** Bulk-update data from API without marking fields dirty.
   *  Pricing and signature are deep-merged so callers can pass partial updates. */
  populateFromApi: (
    updates: Omit<Partial<ApplicationFormData>, "pricing" | "signature"> & {
      pricing?: Partial<PricingData>;
      signature?: Partial<SignatureData>;
    },
  ) => void;
  /** Update a serverId on an array entity without marking dirty */
  setEntityServerId: (
    borrowerType: "primary" | "co",
    entityType:
      | "addresses"
      | "employmentHistory"
      | "financialAccounts"
      | "additionalIncome"
      | "expenses",
    clientId: string,
    serverId: string,
  ) => void;

  // Borrower updates
  updatePrimaryBorrower: (updates: Partial<BorrowerData>) => void;
  updateCoBorrower: (updates: Partial<BorrowerData>) => void;
  /** Update borrower fields from server responses without marking dirty */
  setBorrowerFieldWithoutDirty: (
    borrowerType: "primary" | "co",
    updates: Partial<BorrowerData>,
  ) => void;
  toggleCoBorrower: (hasCoBorrower: boolean) => void;
  /**
   * Atomically clear all co-borrower state after a successful server-side delete.
   * Wipes coBorrower data, hasCoBorrower flag, coBorrowerId, all coBorrower.* dirty
   * fields, all coBorrower.* field errors, pending co.* entity deletes, and resets
   * co-borrower signature state. Does NOT mark anything dirty — the server has
   * already deleted the borrower, so no autosave should fire.
   */
  removeCoBorrower: () => void;

  // Address operations
  addAddress: (borrowerType: "primary" | "co", address: Address) => void;
  updateAddress: (
    borrowerType: "primary" | "co",
    index: number,
    updates: Partial<Address>,
  ) => void;
  removeAddress: (borrowerType: "primary" | "co", index: number) => void;

  // Employment operations
  addEmployment: (
    borrowerType: "primary" | "co",
    employment: Employment,
  ) => void;
  updateEmployment: (
    borrowerType: "primary" | "co",
    index: number,
    updates: Partial<Employment>,
  ) => void;
  removeEmployment: (borrowerType: "primary" | "co", index: number) => void;

  // Financial account operations
  addFinancialAccount: (
    borrowerType: "primary" | "co",
    account: FinancialAccount,
  ) => void;
  updateFinancialAccount: (
    borrowerType: "primary" | "co",
    index: number,
    updates: Partial<FinancialAccount>,
  ) => void;
  removeFinancialAccount: (
    borrowerType: "primary" | "co",
    index: number,
  ) => void;

  // Additional income operations
  addAdditionalIncome: (
    borrowerType: "primary" | "co",
    income: AdditionalIncome,
  ) => void;
  updateAdditionalIncome: (
    borrowerType: "primary" | "co",
    index: number,
    updates: Partial<AdditionalIncome>,
  ) => void;
  removeAdditionalIncome: (
    borrowerType: "primary" | "co",
    index: number,
  ) => void;

  // Expense operations
  addExpense: (borrowerType: "primary" | "co", expense: Expense) => void;
  updateExpense: (
    borrowerType: "primary" | "co",
    index: number,
    updates: Partial<Expense>,
  ) => void;
  removeExpense: (borrowerType: "primary" | "co", index: number) => void;

  // Property updates
  updateHome: (updates: Partial<HomeData>) => void;
  updateLand: (updates: Partial<LandData>) => void;

  // Pricing updates
  updatePricing: (updates: Partial<PricingData>) => void;

  // Lender updates
  updateLenders: (lenders: LenderConfig[]) => void;
  toggleLender: (lenderId: string, enabled: boolean) => void;
  toggleLenderAutoSubmit: (lenderId: string, autoSubmit: boolean) => void;

  // Signature updates
  updateSignature: (updates: Partial<SignatureData>) => void;
  updateBorrowerSignatureStatus: (status: SignatureStatus) => void;
  updateCoBorrowerSignatureStatus: (
    status: SignatureStatus | undefined,
  ) => void;
  resetSignatureForEdit: () => void;
  isLockedForSigning: boolean;

  // Lender submission
  lenderSubmissions: LenderSubmission[];
  submitToLender: (lenderId: string) => void;
  submitToAllLenders: () => void;

  // Application ID management
  updateApplicationIds: (
    applicationId?: string,
    primaryBorrowerId?: string,
  ) => void;
  updateCoBorrowerId: (coBorrowerId: string) => void;

  // Entity deletion tracking
  queueEntityDelete: (
    borrowerType: "primary" | "co",
    entityType: string,
    serverId: string,
  ) => void;
  clearPendingEntityDeletes: (key: string) => void;

  // Field errors
  setFieldError: (field: string, message: string) => void;
  clearFieldError: (field: string) => void;
  setAllFieldErrors: (errors: Record<string, string>) => void;
  setValidationErrors: (errors: Record<string, string>) => void;
  setBackendValidationErrors: (errors: Record<string, string>) => void;
  setManualFieldValidationActive: (active: boolean) => void;
  setFrontendValidationPrimaryPassed: (passed: boolean) => void;
  setFrontendValidationCoPassed: (passed: boolean) => void;

  // Duplicate warning management
  setDuplicateWarning: (fieldPath: string, message: string | null) => void;
  acknowledgeDuplicateWarning: (fieldPath: string) => void;

  // Autosave pause (field-level quarantine after API failure)
  pauseAutoSaveFields: (fields: Set<string>) => void;

  // Save operations
  markFieldDirty: (fieldPath: string) => void;
  clearDirtyFields: () => void;
  /**
   * Remove only the specified field paths from the dirty set (because the
   * backend confirmed them). Unlike clearDirtyFields, fields edited during an
   * in-flight save are preserved so they are never silently dropped.
   */
  clearSavedFields: (fields: Set<string>) => void;
  saveApplication: () => Promise<void>;

  // Lock state operations
  refreshLockState: (
    applicationId: string,
    lockedByUserId: string | null,
  ) => void;

  // Computed values
  getStepProgress: (step: ApplicationStep) => number;
  getOverallProgress: () => number;
  getStepConfigs: () => StepConfig[];
}

export type ApplicationFormStore = ApplicationFormState &
  ApplicationFormActions;

// ============================================================================
// Step Order
// ============================================================================

export const STEP_ORDER: ApplicationStep[] = [
  "summary",
  "primary-borrower",
  "co-borrower",
  "property",
  "pricing",
  "lenders",
  "signature",
];

// ============================================================================
// Context
// ============================================================================

const ApplicationFormContext = React.createContext<ApplicationFormStore | null>(
  null,
);

// ============================================================================
// Provider
// ============================================================================

interface ApplicationFormProviderProps {
  children: React.ReactNode;
  initialApplicationId?: string;
}

function applyUnpauseOnEdit(
  prev: ApplicationFormState,
  newData: ApplicationFormData,
  editedFieldPaths: string[],
): Pick<
  ApplicationFormState,
  "pausedAutoSaveFields" | "pausedAutoSaveSnapshots"
> {
  const pausedAutoSaveFields = new Set(prev.pausedAutoSaveFields);

  for (const fieldPath of editedFieldPaths) {
    if (!pausedAutoSaveFields.has(fieldPath)) continue;
    const newSnapshot = getFieldValueSnapshot(newData, fieldPath);
    if (newSnapshot !== prev.pausedAutoSaveSnapshots[fieldPath]) {
      pausedAutoSaveFields.delete(fieldPath);
    }
  }

  const pausedAutoSaveSnapshots = Object.fromEntries(
    Object.entries(prev.pausedAutoSaveSnapshots).filter(([key]) =>
      pausedAutoSaveFields.has(key),
    ),
  );

  return { pausedAutoSaveFields, pausedAutoSaveSnapshots };
}

export const ApplicationFormProvider: React.FC<
  ApplicationFormProviderProps
> = ({ children, initialApplicationId }) => {
  const [state, setState] = React.useState<ApplicationFormState>({
    data: createEmptyApplication(),
    currentStep: "primary-borrower",
    isLoading: false,
    isDirty: false,
    dirtyFields: new Set(),
    lastSavedAt: null,
    error: null,
    fieldErrors: {},
    validationErrors: {},
    backendValidationErrors: {},
    manualFieldValidationActive: false,
    frontendValidationPrimaryPassed: false,
    frontendValidationCoPassed: false,
    lenderSubmissions: [],
    pendingEntityDeletes: {},
    loanAmountLoading: false,
    isLockedByAnotherUser: false,
    lockedByUserId: null,
    pausedAutoSaveFields: new Set(),
    pausedAutoSaveSnapshots: {},
    duplicateWarnings: {},
    acknowledgedDuplicateFields: new Set(),
  });

  // Helper to update state with dirty tracking
  const updateData = React.useCallback(
    (
      updater: (data: ApplicationFormData) => ApplicationFormData,
      fieldPath: string,
    ) => {
      setState((prev) => {
        const newData = updater(prev.data);
        const unpause = applyUnpauseOnEdit(prev, newData, [fieldPath]);
        return {
          ...prev,
          ...unpause,
          data: newData,
          isDirty: true,
          dirtyFields: new Set([...prev.dirtyFields, fieldPath]),
        };
      });
    },
    [],
  );

  // Navigation actions
  const setCurrentStep = React.useCallback((step: ApplicationStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const goToNextStep = React.useCallback(() => {
    setState((prev) => {
      const currentIndex = STEP_ORDER.indexOf(prev.currentStep);
      const nextIndex = Math.min(currentIndex + 1, STEP_ORDER.length - 1);
      return { ...prev, currentStep: STEP_ORDER[nextIndex] };
    });
  }, []);

  const goToPreviousStep = React.useCallback(() => {
    setState((prev) => {
      const currentIndex = STEP_ORDER.indexOf(prev.currentStep);
      const prevIndex = Math.max(currentIndex - 1, 0);
      return { ...prev, currentStep: STEP_ORDER[prevIndex] };
    });
  }, []);

  // Initialize application
  const initializeApplication = React.useCallback((applicationId?: string) => {
    setState((prev) => ({
      ...prev,
      isLoading: !!applicationId,
      error: null,
      data: {
        ...prev.data,
        applicationId,
      },
    }));
    return Promise.resolve();
  }, []);

  const resetApplication = React.useCallback(() => {
    setState({
      data: createEmptyApplication(),
      currentStep: "primary-borrower",
      isLoading: false,
      isDirty: false,
      dirtyFields: new Set(),
      lastSavedAt: null,
      error: null,
      fieldErrors: {},
      validationErrors: {},
      backendValidationErrors: {},
      manualFieldValidationActive: false,
      frontendValidationPrimaryPassed: false,
      frontendValidationCoPassed: false,
      lenderSubmissions: [],
      pendingEntityDeletes: {},
      loanAmountLoading: false,
      isLockedByAnotherUser: false,
      lockedByUserId: null,
      pausedAutoSaveFields: new Set(),
      pausedAutoSaveSnapshots: {},
      duplicateWarnings: {},
      acknowledgedDuplicateFields: new Set(),
    });
  }, []);

  const pauseAutoSaveFields = React.useCallback((fields: Set<string>) => {
    if (fields.size === 0) return;
    setState((prev) => {
      const pausedAutoSaveFields = new Set(prev.pausedAutoSaveFields);
      const pausedAutoSaveSnapshots = { ...prev.pausedAutoSaveSnapshots };
      for (const field of fields) {
        pausedAutoSaveFields.add(field);
        pausedAutoSaveSnapshots[field] = getFieldValueSnapshot(
          prev.data,
          field,
        );
      }
      return {
        ...prev,
        pausedAutoSaveFields,
        pausedAutoSaveSnapshots,
      };
    });
  }, []);

  // Loading state
  const setIsLoading = React.useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, isLoading: loading }));
  }, []);

  const setLoanAmountLoading = React.useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, loanAmountLoading: loading }));
  }, []);

  // Entity deletion tracking
  const queueEntityDelete = React.useCallback(
    (borrowerType: "primary" | "co", entityType: string, serverId: string) => {
      const delKey = `${borrowerType}.${entityType}`;
      setState((prev) => ({
        ...prev,
        pendingEntityDeletes: {
          ...prev.pendingEntityDeletes,
          [delKey]: [...(prev.pendingEntityDeletes[delKey] || []), serverId],
        },
      }));
    },
    [],
  );

  const clearPendingEntityDeletes = React.useCallback((key: string) => {
    setState((prev) => {
      const { [key]: _, ...rest } = prev.pendingEntityDeletes;
      return { ...prev, pendingEntityDeletes: rest };
    });
  }, []);

  // Field-level errors
  const setFieldError = React.useCallback((field: string, message: string) => {
    setState((prev) => ({
      ...prev,
      fieldErrors: { ...prev.fieldErrors, [field]: message },
    }));
  }, []);

  const clearFieldError = React.useCallback((field: string) => {
    setState((prev) => {
      const { [field]: _fieldError, ...fieldErrors } = prev.fieldErrors;
      const { [field]: _backendError, ...backendValidationErrors } =
        prev.backendValidationErrors;
      if (
        field in prev.fieldErrors === false &&
        field in prev.backendValidationErrors === false
      ) {
        return prev;
      }
      return { ...prev, fieldErrors, backendValidationErrors };
    });
  }, []);

  const setAllFieldErrors = React.useCallback(
    (errors: Record<string, string>) => {
      setState((prev) => ({ ...prev, fieldErrors: errors }));
    },
    [],
  );

  const setValidationErrors = React.useCallback(
    (errors: Record<string, string>) => {
      setState((prev) => {
        const prevKeys = Object.keys(prev.validationErrors);
        const nextKeys = Object.keys(errors);
        if (
          prevKeys.length === nextKeys.length &&
          prevKeys.every((key) => prev.validationErrors[key] === errors[key])
        ) {
          return prev;
        }
        return { ...prev, validationErrors: errors };
      });
    },
    [],
  );

  const setBackendValidationErrors = React.useCallback(
    (errors: Record<string, string>) => {
      setState((prev) => ({ ...prev, backendValidationErrors: errors }));
    },
    [],
  );

  const setManualFieldValidationActive = React.useCallback(
    (active: boolean) => {
      setState((prev) =>
        prev.manualFieldValidationActive === active
          ? prev
          : { ...prev, manualFieldValidationActive: active },
      );
    },
    [],
  );

  const setFrontendValidationPrimaryPassed = React.useCallback(
    (passed: boolean) => {
      setState((prev) =>
        prev.frontendValidationPrimaryPassed === passed
          ? prev
          : { ...prev, frontendValidationPrimaryPassed: passed },
      );
    },
    [],
  );

  const setFrontendValidationCoPassed = React.useCallback((passed: boolean) => {
    setState((prev) =>
      prev.frontendValidationCoPassed === passed
        ? prev
        : { ...prev, frontendValidationCoPassed: passed },
    );
  }, []);

  // Duplicate warning management
  const setDuplicateWarning = React.useCallback(
    (fieldPath: string, message: string | null) => {
      setState((prev) => {
        if (message === null) {
          const { [fieldPath]: _, ...rest } = prev.duplicateWarnings;
          return { ...prev, duplicateWarnings: rest };
        }
        return {
          ...prev,
          duplicateWarnings: {
            ...prev.duplicateWarnings,
            [fieldPath]: message,
          },
        };
      });
    },
    [],
  );

  const acknowledgeDuplicateWarning = React.useCallback((fieldPath: string) => {
    setState((prev) => ({
      ...prev,
      acknowledgedDuplicateFields: new Set([
        ...prev.acknowledgedDuplicateFields,
        fieldPath,
      ]),
    }));
  }, []);

  // Bulk populate from API (does NOT mark fields dirty)
  // Deep-merges known nested objects (pricing, etc.) so callers can pass
  // partial updates without losing fields set by other code paths.
  type PopulateUpdates = Omit<
    Partial<ApplicationFormData>,
    "pricing" | "signature"
  > & {
    pricing?: Partial<PricingData>;
    // Partial so callers can update a subset of signature fields (e.g. only
    // borrowerStatus during init) without overwriting the rest.
    signature?: Partial<SignatureData>;
  };
  const populateFromApi = React.useCallback((updates: PopulateUpdates) => {
    setState((prev) => {
      const {
        pricing: pricingUpdates,
        signature: signatureUpdates,
        ...rest
      } = updates;
      const merged = { ...prev.data, ...rest };
      // Deep-merge pricing and signature so partial updates preserve existing fields
      if (pricingUpdates) {
        merged.pricing = { ...prev.data.pricing, ...pricingUpdates };
      }
      if (signatureUpdates) {
        merged.signature = { ...prev.data.signature, ...signatureUpdates };
      }
      return { ...prev, data: merged };
    });
  }, []);

  // Update serverId on array entity without marking dirty.
  // Matches by client-side `id` instead of index to avoid race conditions
  // when the user adds/removes items while an async save is in flight.
  const setEntityServerId = React.useCallback(
    (
      borrowerType: "primary" | "co",
      entityType:
        | "addresses"
        | "employmentHistory"
        | "financialAccounts"
        | "additionalIncome"
        | "expenses",
      clientId: string,
      serverId: string,
    ) => {
      setState((prev) => {
        const borrower =
          borrowerType === "primary"
            ? prev.data.primaryBorrower
            : prev.data.coBorrower;
        if (!borrower) return prev;

        const arr = [
          ...(borrower[entityType] as Array<{
            id?: string;
            serverId?: string;
          }>),
        ];
        const idx = arr.findIndex((item) => item.id === clientId);
        if (idx >= 0) {
          arr[idx] = { ...arr[idx], serverId };
        }

        const updated = { ...borrower, [entityType]: arr };
        const data =
          borrowerType === "primary"
            ? { ...prev.data, primaryBorrower: updated }
            : { ...prev.data, coBorrower: updated };

        return { ...prev, data };
      });
    },
    [],
  );

  // Borrower updates
  const updatePrimaryBorrower = React.useCallback(
    (updates: Partial<BorrowerData>) => {
      setState((prev) => {
        const editedFieldPaths = Object.keys(updates).map(
          (key) => `primaryBorrower.${key}`,
        );
        const newData = {
          ...prev.data,
          primaryBorrower: { ...prev.data.primaryBorrower, ...updates },
        };
        const unpause = applyUnpauseOnEdit(prev, newData, editedFieldPaths);
        const newDirtyFields = new Set(prev.dirtyFields);
        for (const path of editedFieldPaths) {
          newDirtyFields.add(path);
        }
        return {
          ...prev,
          ...unpause,
          data: newData,
          isDirty: true,
          dirtyFields: newDirtyFields,
        };
      });
    },
    [],
  );

  const updateCoBorrower = React.useCallback(
    (updates: Partial<BorrowerData>) => {
      setState((prev) => {
        const editedFieldPaths = Object.keys(updates).map(
          (key) => `coBorrower.${key}`,
        );
        const newData = {
          ...prev.data,
          coBorrower: {
            ...(prev.data.coBorrower || createEmptyBorrower()),
            ...updates,
          },
        };
        const unpause = applyUnpauseOnEdit(prev, newData, editedFieldPaths);
        const newDirtyFields = new Set(prev.dirtyFields);
        for (const path of editedFieldPaths) {
          newDirtyFields.add(path);
        }
        return {
          ...prev,
          ...unpause,
          data: newData,
          isDirty: true,
          dirtyFields: newDirtyFields,
        };
      });
    },
    [],
  );

  const setBorrowerFieldWithoutDirty = React.useCallback(
    (borrowerType: "primary" | "co", updates: Partial<BorrowerData>) => {
      setState((prev) => {
        const borrower =
          borrowerType === "primary"
            ? prev.data.primaryBorrower
            : prev.data.coBorrower;
        if (!borrower) return prev;

        const updated = { ...borrower, ...updates };
        const data =
          borrowerType === "primary"
            ? { ...prev.data, primaryBorrower: updated }
            : {
                ...prev.data,
                coBorrower: updated,
              };

        return { ...prev, data };
      });
    },
    [],
  );

  const toggleCoBorrower = React.useCallback((hasCoBorrower: boolean) => {
    // Co-borrower presence is persisted by createCoBorrower/removeCoBorrower API
    // calls — not a separate autosave field.
    setState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        hasCoBorrower,
        coBorrower: hasCoBorrower
          ? prev.data.coBorrower || createEmptyBorrower()
          : undefined,
      },
    }));
  }, []);

  const removeCoBorrower = React.useCallback(() => {
    setState((prev) => {
      const newDirtyFields = new Set<string>();
      for (const f of prev.dirtyFields) {
        if (!f.startsWith("coBorrower.") && f !== "hasCoBorrower") {
          newDirtyFields.add(f);
        }
      }

      const newFieldErrors: Record<string, string> = {};
      for (const [k, v] of Object.entries(prev.fieldErrors)) {
        if (!k.startsWith("coBorrower.")) {
          newFieldErrors[k] = v;
        }
      }

      const newPendingEntityDeletes: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(prev.pendingEntityDeletes)) {
        if (!k.startsWith("co.")) {
          newPendingEntityDeletes[k] = v;
        }
      }

      const pausedAutoSaveFields = new Set<string>();
      const pausedAutoSaveSnapshots: Record<string, string> = {};
      for (const f of prev.pausedAutoSaveFields) {
        if (!f.startsWith("coBorrower.") && f !== "hasCoBorrower") {
          pausedAutoSaveFields.add(f);
          pausedAutoSaveSnapshots[f] = prev.pausedAutoSaveSnapshots[f];
        }
      }

      return {
        ...prev,
        data: {
          ...prev.data,
          hasCoBorrower: false,
          coBorrower: undefined,
          coBorrowerId: undefined,
          signature: {
            ...prev.data.signature,
            coBorrowerStatus: undefined,
            coBorrowerSignedAt: undefined,
          },
        },
        dirtyFields: newDirtyFields,
        isDirty: newDirtyFields.size > 0,
        fieldErrors: newFieldErrors,
        pendingEntityDeletes: newPendingEntityDeletes,
        pausedAutoSaveFields,
        pausedAutoSaveSnapshots,
      };
    });
  }, []);

  // Generic array operations helper
  const updateBorrowerArray = React.useCallback(
    <T,>(
      borrowerType: "primary" | "co",
      arrayKey: keyof BorrowerData,
      updater: (arr: T[]) => T[],
    ) => {
      const fieldPath = `${borrowerType === "primary" ? "primaryBorrower" : "coBorrower"}.${String(arrayKey)}`;
      updateData((data) => {
        const borrower =
          borrowerType === "primary" ? data.primaryBorrower : data.coBorrower;
        if (!borrower) return data;

        const updated = {
          ...borrower,
          [arrayKey]: updater((borrower[arrayKey] as T[]) || []),
        };

        return borrowerType === "primary"
          ? { ...data, primaryBorrower: updated }
          : { ...data, coBorrower: updated };
      }, fieldPath);
    },
    [updateData],
  );

  // Address operations
  const addAddress = React.useCallback(
    (borrowerType: "primary" | "co", address: Address) => {
      const prefix =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      setState((prev) => {
        const borrower =
          borrowerType === "primary"
            ? prev.data.primaryBorrower
            : prev.data.coBorrower;
        if (!borrower) return prev;
        const newIndex = borrower.addresses.length;
        const newAddresses = [...borrower.addresses, address];
        const updated = { ...borrower, addresses: newAddresses };
        const newData =
          borrowerType === "primary"
            ? { ...prev.data, primaryBorrower: updated }
            : { ...prev.data, coBorrower: updated };
        const fieldPath = `${prefix}.addresses.${newIndex}`;
        const unpause = applyUnpauseOnEdit(prev, newData, [fieldPath]);
        return {
          ...prev,
          ...unpause,
          data: newData,
          isDirty: true,
          dirtyFields: new Set([...prev.dirtyFields, fieldPath]),
        };
      });
    },
    [],
  );

  const updateAddress = React.useCallback(
    (
      borrowerType: "primary" | "co",
      index: number,
      updates: Partial<Address>,
    ) => {
      const prefix =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      const fieldPath = `${prefix}.addresses.${index}`;
      updateData((data) => {
        const borrower =
          borrowerType === "primary" ? data.primaryBorrower : data.coBorrower;
        if (!borrower) return data;
        const newAddresses = borrower.addresses.map((addr, i) =>
          i === index ? { ...addr, ...updates } : addr,
        );
        const updated = { ...borrower, addresses: newAddresses };
        return borrowerType === "primary"
          ? { ...data, primaryBorrower: updated }
          : { ...data, coBorrower: updated };
      }, fieldPath);
    },
    [updateData],
  );

  const removeAddress = React.useCallback(
    (borrowerType: "primary" | "co", index: number) => {
      setState((prev) => {
        const bKey =
          borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
        const borrower = prev.data[bKey];
        if (!borrower) return prev;

        const addr = borrower.addresses[index];
        const delKey = `${borrowerType}.addresses`;
        const newPendingEntityDeletes = { ...prev.pendingEntityDeletes };
        const newDirtyFields = new Set(prev.dirtyFields);

        if (addr?.serverId) {
          newPendingEntityDeletes[delKey] = [
            ...(newPendingEntityDeletes[delKey] || []),
            addr.serverId,
          ];
          newDirtyFields.add(`${bKey}.addresses._delete`);
        }

        newDirtyFields.delete(`${bKey}.addresses`);
        for (const f of prev.dirtyFields) {
          if (f.match(new RegExp(`^${bKey}\\.addresses\\.${index}(\\.|$)`))) {
            newDirtyFields.delete(f);
          }
        }

        const newAddresses = borrower.addresses.filter((_, i) => i !== index);
        const updated = { ...borrower, addresses: newAddresses };
        const data =
          borrowerType === "primary"
            ? { ...prev.data, primaryBorrower: updated }
            : { ...prev.data, coBorrower: updated };

        return {
          ...prev,
          data,
          pendingEntityDeletes: newPendingEntityDeletes,
          dirtyFields: newDirtyFields,
          isDirty: newDirtyFields.size > 0,
        };
      });
    },
    [],
  );

  // Employment operations
  const addEmployment = React.useCallback(
    (borrowerType: "primary" | "co", employment: Employment) => {
      updateBorrowerArray<Employment>(
        borrowerType,
        "employmentHistory",
        (arr) => [...arr, employment],
      );
    },
    [updateBorrowerArray],
  );

  const updateEmployment = React.useCallback(
    (
      borrowerType: "primary" | "co",
      index: number,
      updates: Partial<Employment>,
    ) => {
      updateBorrowerArray<Employment>(
        borrowerType,
        "employmentHistory",
        (arr) =>
          arr.map((item, i) => (i === index ? { ...item, ...updates } : item)),
      );
    },
    [updateBorrowerArray],
  );

  const removeEmployment = React.useCallback(
    (borrowerType: "primary" | "co", index: number) => {
      // Capture serverId before filtering so we can queue a server DELETE
      setState((prev) => {
        const bKey =
          borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
        const emp = prev.data[bKey]?.employmentHistory[index];
        if (emp?.serverId) {
          const delKey = `${borrowerType}.employmentHistory`;
          return {
            ...prev,
            pendingEntityDeletes: {
              ...prev.pendingEntityDeletes,
              [delKey]: [
                ...(prev.pendingEntityDeletes[delKey] || []),
                emp.serverId,
              ],
            },
          };
        }
        return prev;
      });
      updateBorrowerArray<Employment>(
        borrowerType,
        "employmentHistory",
        (arr) => arr.filter((_, i) => i !== index),
      );
    },
    [updateBorrowerArray],
  );

  // Financial account operations
  const addFinancialAccount = React.useCallback(
    (borrowerType: "primary" | "co", account: FinancialAccount) => {
      const prefix =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      setState((prev) => {
        const borrower =
          borrowerType === "primary"
            ? prev.data.primaryBorrower
            : prev.data.coBorrower;
        if (!borrower) return prev;
        const newIndex = borrower.financialAccounts.length;
        const newAccounts = [...borrower.financialAccounts, account];
        const updated = { ...borrower, financialAccounts: newAccounts };
        const newData =
          borrowerType === "primary"
            ? { ...prev.data, primaryBorrower: updated }
            : { ...prev.data, coBorrower: updated };
        return {
          ...prev,
          data: newData,
          isDirty: true,
          dirtyFields: new Set([
            ...prev.dirtyFields,
            `${prefix}.financialAccounts.${newIndex}`,
          ]),
        };
      });
    },
    [],
  );

  const updateFinancialAccount = React.useCallback(
    (
      borrowerType: "primary" | "co",
      index: number,
      updates: Partial<FinancialAccount>,
    ) => {
      const prefix =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      const fieldPath = `${prefix}.financialAccounts.${index}`;
      updateData((data) => {
        const borrower =
          borrowerType === "primary" ? data.primaryBorrower : data.coBorrower;
        if (!borrower) return data;
        const newAccounts = borrower.financialAccounts.map((item, i) =>
          i === index ? { ...item, ...updates } : item,
        );
        const updated = { ...borrower, financialAccounts: newAccounts };
        return borrowerType === "primary"
          ? { ...data, primaryBorrower: updated }
          : { ...data, coBorrower: updated };
      }, fieldPath);
    },
    [updateData],
  );

  const removeFinancialAccount = React.useCallback(
    (borrowerType: "primary" | "co", index: number) => {
      // Capture serverId before filtering so we can queue a server DELETE
      setState((prev) => {
        const bKey =
          borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
        const acct = prev.data[bKey]?.financialAccounts[index];
        if (acct?.serverId) {
          const delKey = `${borrowerType}.financialAccounts`;
          return {
            ...prev,
            pendingEntityDeletes: {
              ...prev.pendingEntityDeletes,
              [delKey]: [
                ...(prev.pendingEntityDeletes[delKey] || []),
                acct.serverId,
              ],
            },
          };
        }
        return prev;
      });
      updateBorrowerArray<FinancialAccount>(
        borrowerType,
        "financialAccounts",
        (arr) => arr.filter((_, i) => i !== index),
      );
    },
    [updateBorrowerArray],
  );

  // Additional income operations
  const addAdditionalIncome = React.useCallback(
    (borrowerType: "primary" | "co", income: AdditionalIncome) => {
      const prefix =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      setState((prev) => {
        const borrower =
          borrowerType === "primary"
            ? prev.data.primaryBorrower
            : prev.data.coBorrower;
        if (!borrower) return prev;
        const newIndex = borrower.additionalIncome.length;
        const newIncomes = [...borrower.additionalIncome, income];
        const updated = { ...borrower, additionalIncome: newIncomes };
        const newData =
          borrowerType === "primary"
            ? { ...prev.data, primaryBorrower: updated }
            : { ...prev.data, coBorrower: updated };
        return {
          ...prev,
          data: newData,
          isDirty: true,
          dirtyFields: new Set([
            ...prev.dirtyFields,
            `${prefix}.additionalIncome.${newIndex}`,
          ]),
        };
      });
    },
    [],
  );

  const updateAdditionalIncome = React.useCallback(
    (
      borrowerType: "primary" | "co",
      index: number,
      updates: Partial<AdditionalIncome>,
    ) => {
      const prefix =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      const fieldPath = `${prefix}.additionalIncome.${index}`;
      updateData((data) => {
        const borrower =
          borrowerType === "primary" ? data.primaryBorrower : data.coBorrower;
        if (!borrower) return data;
        const newIncomes = borrower.additionalIncome.map((item, i) =>
          i === index ? { ...item, ...updates } : item,
        );
        const updated = { ...borrower, additionalIncome: newIncomes };
        return borrowerType === "primary"
          ? { ...data, primaryBorrower: updated }
          : { ...data, coBorrower: updated };
      }, fieldPath);
    },
    [updateData],
  );

  const removeAdditionalIncome = React.useCallback(
    (borrowerType: "primary" | "co", index: number) => {
      // Capture serverId before filtering so we can queue a server DELETE
      setState((prev) => {
        const bKey =
          borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
        const inc = prev.data[bKey]?.additionalIncome[index];
        if (inc?.serverId) {
          const delKey = `${borrowerType}.additionalIncome`;
          return {
            ...prev,
            pendingEntityDeletes: {
              ...prev.pendingEntityDeletes,
              [delKey]: [
                ...(prev.pendingEntityDeletes[delKey] || []),
                inc.serverId,
              ],
            },
          };
        }
        return prev;
      });
      updateBorrowerArray<AdditionalIncome>(
        borrowerType,
        "additionalIncome",
        (arr) => arr.filter((_, i) => i !== index),
      );
    },
    [updateBorrowerArray],
  );

  // Expense operations
  const addExpense = React.useCallback(
    (borrowerType: "primary" | "co", expense: Expense) => {
      const prefix =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      setState((prev) => {
        const borrower =
          borrowerType === "primary"
            ? prev.data.primaryBorrower
            : prev.data.coBorrower;
        if (!borrower) return prev;
        const newIndex = borrower.expenses.length;
        const newExpenses = [...borrower.expenses, expense];
        const updated = { ...borrower, expenses: newExpenses };
        const newData =
          borrowerType === "primary"
            ? { ...prev.data, primaryBorrower: updated }
            : { ...prev.data, coBorrower: updated };
        return {
          ...prev,
          data: newData,
          isDirty: true,
          dirtyFields: new Set([
            ...prev.dirtyFields,
            `${prefix}.expenses.${newIndex}`,
          ]),
        };
      });
    },
    [],
  );

  const updateExpense = React.useCallback(
    (
      borrowerType: "primary" | "co",
      index: number,
      updates: Partial<Expense>,
    ) => {
      const prefix =
        borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
      const fieldPath = `${prefix}.expenses.${index}`;
      updateData((data) => {
        const borrower =
          borrowerType === "primary" ? data.primaryBorrower : data.coBorrower;
        if (!borrower) return data;
        const newExpenses = borrower.expenses.map((item, i) =>
          i === index ? { ...item, ...updates } : item,
        );
        const updated = { ...borrower, expenses: newExpenses };
        return borrowerType === "primary"
          ? { ...data, primaryBorrower: updated }
          : { ...data, coBorrower: updated };
      }, fieldPath);
    },
    [updateData],
  );

  const removeExpense = React.useCallback(
    (borrowerType: "primary" | "co", index: number) => {
      // Capture serverId before filtering so we can queue a server DELETE
      setState((prev) => {
        const bKey =
          borrowerType === "primary" ? "primaryBorrower" : "coBorrower";
        const exp = prev.data[bKey]?.expenses[index];
        if (exp?.serverId) {
          const delKey = `${borrowerType}.expenses`;
          return {
            ...prev,
            pendingEntityDeletes: {
              ...prev.pendingEntityDeletes,
              [delKey]: [
                ...(prev.pendingEntityDeletes[delKey] || []),
                exp.serverId,
              ],
            },
          };
        }
        return prev;
      });
      updateBorrowerArray<Expense>(borrowerType, "expenses", (arr) =>
        arr.filter((_, i) => i !== index),
      );
    },
    [updateBorrowerArray],
  );

  // Property updates
  const updateHome = React.useCallback(
    (updates: Partial<HomeData>) => {
      updateData(
        (data) => ({ ...data, home: { ...data.home, ...updates } }),
        "home",
      );
    },
    [updateData],
  );

  const updateLand = React.useCallback(
    (updates: Partial<LandData>) => {
      updateData(
        (data) => ({
          ...data,
          land: {
            ...data.land,
            ...updates,
            supportedByPorchpass:
              updates.supportedByPorchpass !== undefined
                ? updates.supportedByPorchpass
                : undefined,
            blockReasons:
              updates.blockReasons !== undefined
                ? updates.blockReasons
                : undefined,
          },
        }),
        "land",
      );
    },
    [updateData],
  );

  // Pricing updates
  const updatePricing = React.useCallback(
    (updates: Partial<PricingData>) => {
      updateData(
        (data) => ({ ...data, pricing: { ...data.pricing, ...updates } }),
        "pricing",
      );
    },
    [updateData],
  );

  // Lender updates
  const updateLenders = React.useCallback(
    (lenders: LenderConfig[]) => {
      updateData((data) => ({ ...data, lenders }), "lenders");
    },
    [updateData],
  );

  const toggleLender = React.useCallback(
    (lenderId: string, enabled: boolean) => {
      updateData(
        (data) => ({
          ...data,
          lenders: data.lenders.map((l) =>
            l.lenderId === lenderId ? { ...l, enabled } : l,
          ),
        }),
        "lenders",
      );
    },
    [updateData],
  );

  const toggleLenderAutoSubmit = React.useCallback(
    (lenderId: string, autoSubmit: boolean) => {
      updateData(
        (data) => ({
          ...data,
          lenders: data.lenders.map((l) =>
            l.lenderId === lenderId ? { ...l, autoSubmit } : l,
          ),
        }),
        "lenders",
      );
    },
    [updateData],
  );

  // Signature updates
  const updateSignature = React.useCallback(
    (updates: Partial<SignatureData>) => {
      updateData(
        (data) => ({ ...data, signature: { ...data.signature, ...updates } }),
        "signature",
      );
    },
    [updateData],
  );

  const updateBorrowerSignatureStatus = React.useCallback(
    (status: SignatureStatus) => {
      updateData(
        (data) => ({
          ...data,
          signature: {
            ...data.signature,
            borrowerStatus: status,
            borrowerSignedAt:
              status === "signed"
                ? new Date().toISOString()
                : data.signature.borrowerSignedAt,
          },
        }),
        "signature.borrowerStatus",
      );
    },
    [updateData],
  );

  const updateCoBorrowerSignatureStatus = React.useCallback(
    (status: SignatureStatus | undefined) => {
      updateData(
        (data) => ({
          ...data,
          signature: {
            ...data.signature,
            coBorrowerStatus: status,
            coBorrowerSignedAt:
              status === "signed"
                ? new Date().toISOString()
                : data.signature.coBorrowerSignedAt,
          },
        }),
        "signature.coBorrowerStatus",
      );
    },
    [updateData],
  );

  // Reset signature for edit (void envelope and unlock)
  const resetSignatureForEdit = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        signature: {
          ...prev.data.signature,
          borrowerStatus: "not-ready",
          coBorrowerStatus: undefined,
          borrowerSignedAt: undefined,
          coBorrowerSignedAt: undefined,
          envelopeId: undefined,
        },
      },
    }));
  }, []);

  // Compute isLockedForSigning from application + borrower signature status
  // (persists across modal close/open)
  const isLockedForSigning = React.useMemo(
    () =>
      isApplicationLockedForSigning(
        state.data.signature,
        state.data.hasCoBorrower,
      ),
    [state.data.signature, state.data.hasCoBorrower],
  );

  // Lender submission
  const submitToLender = React.useCallback((lenderId: string) => {
    // Mark as submitting
    setState((prev) => ({
      ...prev,
      lenderSubmissions: prev.lenderSubmissions.map((l) =>
        l.lenderId === lenderId
          ? {
              ...l,
              submissionStatus: "submitted" as const,
              submittedAt: new Date().toISOString(),
            }
          : l,
      ),
    }));
  }, []);

  const submitToAllLenders = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      lenderSubmissions: prev.lenderSubmissions.map((l) => ({
        ...l,
        submissionStatus: "submitted" as const,
        submittedAt: new Date().toISOString(),
      })),
    }));
  }, []);

  // Initialize lender submissions when lenders change
  React.useEffect(() => {
    if (state.data.lenders.length > 0 && state.lenderSubmissions.length === 0) {
      setState((prev) => ({
        ...prev,
        lenderSubmissions: prev.data.lenders
          .filter((l) => l.enabled)
          .map((l) => ({
            ...l,
            submissionStatus: "pending" as const,
          })),
      }));
    }
  }, [state.data.lenders, state.lenderSubmissions.length]);

  // Application ID management
  const updateApplicationIds = React.useCallback(
    (applicationId?: string, primaryBorrowerId?: string) => {
      setState((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          applicationId: applicationId ?? prev.data.applicationId,
          primaryBorrowerId: primaryBorrowerId ?? prev.data.primaryBorrowerId,
        },
      }));
    },
    [],
  );

  const updateCoBorrowerId = React.useCallback((coBorrowerId: string) => {
    setState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        coBorrowerId,
      },
    }));
  }, []);

  // Save operations
  const markFieldDirty = React.useCallback((fieldPath: string) => {
    setState((prev) => ({
      ...prev,
      isDirty: true,
      dirtyFields: new Set([...prev.dirtyFields, fieldPath]),
    }));
  }, []);

  const clearDirtyFields = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      isDirty: false,
      dirtyFields: new Set(),
    }));
  }, []);

  const clearSavedFields = React.useCallback((fields: Set<string>) => {
    if (fields.size === 0) return;
    setState((prev) => {
      const next = new Set(prev.dirtyFields);
      fields.forEach((f) => next.delete(f));
      return { ...prev, dirtyFields: next, isDirty: next.size > 0 };
    });
  }, []);

  const saveApplication = React.useCallback(() => {
    // Saving is handled by the debounced auto-save hook with API calls.
    // This method is kept for interface compatibility.
    setState((prev) => ({
      ...prev,
      isDirty: false,
      dirtyFields: new Set(),
      lastSavedAt: new Date().toISOString(),
    }));
    return Promise.resolve();
  }, []);

  // Lock state operations
  const refreshLockState = React.useCallback(
    (_applicationId: string, lockedByUserId: string | null) => {
      // Update lock state based on the provided lockedByUserId
      // The actual API call is done by the useApplicationLock hook
      setState((prev) => ({
        ...prev,
        isLockedByAnotherUser: !!lockedByUserId,
        lockedByUserId,
      }));
    },
    [],
  );

  // Progress calculations
  const getStepProgress = React.useCallback(
    (step: ApplicationStep): number => {
      return computeApplicationStepProgress(
        state.data,
        step,
        state.validationErrors,
      );
    },
    [state.data, state.validationErrors],
  );

  const getOverallProgress = React.useCallback((): number => {
    return computeApplicationOverallProgress(
      state.data,
      state.validationErrors,
    );
  }, [state.data, state.validationErrors]);

  const getStepConfigs = React.useCallback((): StepConfig[] => {
    return STEP_ORDER.map((step) => {
      const progress = getStepProgress(step);
      let label = "";
      let description = "";

      switch (step) {
        case "summary":
          label = "Summary";
          description = "";
          break;
        case "primary-borrower":
          label = "Primary Borrower";
          description = "Primary borrower personal information.";
          break;
        case "co-borrower":
          label = "Co-Borrower";
          description = "Optional";
          break;
        case "property":
          label = "Property";
          description = "Property details and land specifics.";
          break;
        case "pricing":
          label = "Pricing";
          description =
            "Home price, upgrades, site improvements, and loan details.";
          break;
        case "lenders":
          label = "Lenders";
          description = "Manage lenders, submission automations, and defaults.";
          break;
        case "signature":
          label = "Signatures";
          description = "Manage application signatures via Docusign.";
          break;
      }

      return {
        id: step,
        label,
        description,
        completed: progress === 100,
        progress,
        disabled: false,
      };
    });
  }, [getStepProgress]);

  // Initialize on mount
  React.useEffect(() => {
    if (initialApplicationId) {
      void initializeApplication(initialApplicationId);
    }
  }, [initialApplicationId, initializeApplication]);

  const store: ApplicationFormStore = {
    ...state,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    initializeApplication,
    resetApplication,
    setIsLoading,
    setLoanAmountLoading,
    populateFromApi,
    setEntityServerId,
    updatePrimaryBorrower,
    updateCoBorrower,
    setBorrowerFieldWithoutDirty,
    toggleCoBorrower,
    removeCoBorrower,
    addAddress,
    updateAddress,
    removeAddress,
    addEmployment,
    updateEmployment,
    removeEmployment,
    addFinancialAccount,
    updateFinancialAccount,
    removeFinancialAccount,
    addAdditionalIncome,
    updateAdditionalIncome,
    removeAdditionalIncome,
    addExpense,
    updateExpense,
    removeExpense,
    updateHome,
    updateLand,
    updatePricing,
    updateLenders,
    toggleLender,
    toggleLenderAutoSubmit,
    updateSignature,
    updateBorrowerSignatureStatus,
    updateCoBorrowerSignatureStatus,
    resetSignatureForEdit,
    isLockedForSigning,
    lenderSubmissions: state.lenderSubmissions,
    submitToLender,
    submitToAllLenders,
    updateApplicationIds,
    updateCoBorrowerId,
    queueEntityDelete,
    clearPendingEntityDeletes,
    setFieldError,
    clearFieldError,
    setAllFieldErrors,
    setValidationErrors,
    setBackendValidationErrors,
    setManualFieldValidationActive,
    setFrontendValidationPrimaryPassed,
    setFrontendValidationCoPassed,
    setDuplicateWarning,
    acknowledgeDuplicateWarning,
    pauseAutoSaveFields,
    markFieldDirty,
    clearDirtyFields,
    clearSavedFields,
    saveApplication,
    refreshLockState,
    getStepProgress,
    getOverallProgress,
    getStepConfigs,
  };

  return (
    <ApplicationFormContext.Provider value={store}>
      {children}
    </ApplicationFormContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useApplicationFormStore = (): ApplicationFormStore => {
  const context = React.useContext(ApplicationFormContext);
  if (!context) {
    throw new Error(
      "useApplicationFormStore must be used within an ApplicationFormProvider",
    );
  }
  return context;
};

export { ApplicationFormContext };
